import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { useAuth } from '@/hooks/use-auth'
import { useCompanies } from '@/hooks/use-companies'
import { useDiagnosisRegistrationStore } from '@/pages/demands/contexts/diagnosis-registration-store-context'
import { queryClient } from '@/services/query-client'

import { GET_ACTIVITIES_QUERY } from '../../queries/use-get-activities'
import { GET_CALENDAR_DIAGNOSES_QUERY } from '../../queries/use-get-calendar-diagnoses'
import { Manager } from './manager'
import { DiagnosisMapper } from './manager/diagnosis-mapper'
import { GeoTagAdapter } from './manager/geotag-adapter'
import { ImageCountMapper } from './manager/image-count-mapper'
import { MappingDiagnosisMapper } from './manager/mapping-diagnosis-mapper'
import { MosaicAdapter } from './manager/mosaic-adapter'
import { PerimeterAdapter } from './manager/perimeter-adapter'
import { RawImagesAdapter } from './manager/raw-images-adapter'
import { SignedUrlsQueue } from './manager/signed-urls-queue'
import { UploadQueue } from './manager/upload-queue'

interface UseCreateDiagnosisProps {
  onUploadProgressByInput: (
    inputType: string,
    progress: { progress?: number; length?: number },
  ) => void
}

export const useCreateDiagnosis = ({
  onUploadProgressByInput,
}: UseCreateDiagnosisProps) => {
  const { current: abortController } = useRef(new AbortController())
  const auth = useAuth()
  const companies = useCompanies()

  const diagnosisRegistrationStore = useDiagnosisRegistrationStore()

  const mutation = useMutation({
    mutationFn: () => {
      const retryCount = diagnosisRegistrationStore.retryCount || 0

      if (retryCount >= 5) {
        diagnosisRegistrationStore.reset()
        throw new Error(
          'Cadastre uma nova demanda, número tentativas excedida.',
        )
      }

      diagnosisRegistrationStore.setRetryCount(retryCount + 1)

      const mosaicAdapter = new MosaicAdapter({
        registerData: diagnosisRegistrationStore.registerData,
        selectFilesData: diagnosisRegistrationStore.selectFilesData,
      })

      const mappingIdMapper = new MappingDiagnosisMapper({
        registerData: diagnosisRegistrationStore.registerData,
      })

      const imageCountMapper = new ImageCountMapper({
        selectFilesData: diagnosisRegistrationStore.selectFilesData,
      })

      const diagnosisMapper = new DiagnosisMapper({
        user: auth.user,
        selectedCompany: companies.selectedCompany,
        registerData: diagnosisRegistrationStore.registerData,
        polygonsData: diagnosisRegistrationStore.polygonsData,
        selectFilesData: diagnosisRegistrationStore.selectFilesData,
        physiologicalRegisterData:
          diagnosisRegistrationStore.physiologicalRegisterData,
        plantingFailureData: diagnosisRegistrationStore.plantingFailureData,

        mosaicAdapter,
        mappingIdMapper,
        imageCountMapper,
      })

      const perimeterAdapter = new PerimeterAdapter({
        selectFilesData: diagnosisRegistrationStore.selectFilesData,
      })
      const geoTagAdapter = new GeoTagAdapter({
        registerData: diagnosisRegistrationStore.registerData,
        selectFilesData: diagnosisRegistrationStore.selectFilesData,
      })

      const rawImagesAdapter = new RawImagesAdapter({
        registerData: diagnosisRegistrationStore.registerData,
        selectFilesData: diagnosisRegistrationStore.selectFilesData,
        imageCountMapper,
      })

      const signedUrlsQueue = new SignedUrlsQueue({
        registerData: diagnosisRegistrationStore.registerData,
      })

      const uploadQueue = new UploadQueue({
        abortController,
        onUploadProgressByInput,
        onFileDone: diagnosisRegistrationStore.markFileAsDone,
      })

      const manager = new Manager({
        abortController,

        registerData: diagnosisRegistrationStore.registerData,
        diagnosisConfirmed: diagnosisRegistrationStore.diagnosisConfirmed,
        diagnosisCreationData: diagnosisRegistrationStore.diagnosisCreationData,
        filesToUpload: diagnosisRegistrationStore.filesToUpload,

        onFilesToUpload: diagnosisRegistrationStore.setFilesToUpload,
        onDiagnosisCreated: diagnosisRegistrationStore.setDiagnosisCreationData,
        onDiagnosisConfirmed:
          diagnosisRegistrationStore.markDiagnosisAsConfirmed,

        diagnosisMapper,
        perimeterAdapter,
        mosaicAdapter,
        geoTagAdapter,
        rawImagesAdapter,
        signedUrlsQueue,
        uploadQueue,
      })

      return manager.execute()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(GET_ACTIVITIES_QUERY)
      queryClient.invalidateQueries(GET_CALENDAR_DIAGNOSES_QUERY)
    },
    onError: (err: any) => {
      if (err instanceof axios.Cancel) return

      let message = err?.response?.data?.message || err.message

      if (err?.code === 'ERR_NETWORK' || !navigator.onLine) {
        message = 'Verifique sua conexão com a internet'
      }

      toast.error(message, {
        autoClose: 1000 * 10, // 10s
      })
    },
  })

  useEffect(() => {
    abortController.signal.addEventListener('abort', () => {
      diagnosisRegistrationStore.resetUploads()
    })

    return () => {
      abortController.abort()
    }
  }, [])

  return mutation
}
